import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../services/api";
import { toast, ToastContainer } from "react-toastify";

const ExamResultDetailsLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const resultId = searchParams.get("resultId");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch result details
  useEffect(() => {
    const fetchResultDetails = async () => {
      if (!resultId) {
        setError("No result ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.exams.getResultById(resultId);

        if (response.success) {
          setResult(response.data);
        } else {
          setError(response.message || "Failed to fetch result details");
        }
      } catch (err) {
        setError("Error connecting to the server");
        console.error("Error fetching result details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResultDetails();
  }, [resultId]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  // Calculate percentage
  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // Navigate back to the exam results page
  const handleBack = () => {
    if (result?.exam?.id) {
      navigate(`/exam-results?examId=${result.exam.id}`);
    } else {
      navigate("/all-exams");
    }
  };

  return (
    <div className="container-fluid">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-lg fw-semibold mb-0">
                  {result
                    ? `Result Details: ${result.exam.title}`
                    : "Result Details"}
                </h6>
                {result && (
                  <p className="text-muted mb-0 mt-1">
                    {result.exam.description ||
                      `${result.exam.difficulty} difficulty`}
                  </p>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleBack}
              >
                <Icon icon="akar-icons:arrow-left" className="me-2" />
                Back to Results
              </button>
            </div>
            <div className="card-body p-24">
              {loading && (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {!loading && !error && !result && (
                <div className="alert alert-info" role="alert">
                  No result found with the provided ID.
                </div>
              )}

              {!loading && !error && result && (
                <>
                  {/* Result Summary */}
                  <div className="row mb-4">
                    <div className="col-lg-8">
                      <div className="card mb-4">
                        <div className="card-header py-3 bg-base">
                          <h6 className="mb-0 fw-semibold">Result Summary</h6>
                        </div>
                        <div className="card-body">
                          <div className="row gy-3">
                            <div className="col-sm-6">
                              <h6 className="mb-0">Student</h6>
                              <p className="text-muted">
                                {result.user.firstName} {result.user.lastName}
                              </p>
                            </div>
                            <div className="col-sm-6">
                              <h6 className="mb-0">Email</h6>
                              <p className="text-muted">{result.user.email}</p>
                            </div>
                            <div className="col-sm-6">
                              <h6 className="mb-0">Exam</h6>
                              <p className="text-muted">{result.exam.title}</p>
                            </div>
                            <div className="col-sm-6">
                              <h6 className="mb-0">Date Taken</h6>
                              <p className="text-muted">
                                {formatDate(result.completedAt)}
                              </p>
                            </div>
                            <div className="col-sm-6">
                              <h6 className="mb-0">Score</h6>
                              <p className="text-muted">
                                {result.score}% (Passing Score:{" "}
                                {result.exam.passPercentage}%)
                              </p>
                            </div>
                            <div className="col-sm-6">
                              <h6 className="mb-0">Status</h6>
                              <span
                                className={`badge ${
                                  result.passed ? "bg-success" : "bg-danger"
                                }`}
                              >
                                {result.passed ? "Passed" : "Failed"}
                              </span>
                            </div>
                            <div className="col-sm-6">
                              <h6 className="mb-0">Total Time</h6>
                              <p className="text-muted">
                                {formatDuration(result.timeSpent)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4">
                      <div className="card">
                        <div className="card-header py-3 bg-base">
                          <h6 className="mb-0 fw-semibold">Performance</h6>
                        </div>
                        <div className="card-body text-center">
                          <div
                            className="position-relative d-inline-block"
                            style={{ width: "160px", height: "160px" }}
                          >
                            <div className="position-absolute top-50 start-50 translate-middle text-center">
                              <h1 className="mb-0 fw-bold">{result.score}%</h1>
                              <p className="mb-0 small text-muted">Score</p>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between mt-4">
                            <div>
                              <h6 className="mb-1">Correct Answers</h6>
                              <p className="mb-0 fs-5 fw-semibold text-success">
                                {result.answers?.filter((a) => a.isCorrect)
                                  .length || 0}
                              </p>
                            </div>
                            <div>
                              <h6 className="mb-1">Incorrect Answers</h6>
                              <p className="mb-0 fs-5 fw-semibold text-danger">
                                {result.answers?.filter((a) => !a.isCorrect)
                                  .length || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Answer Details */}
                  {result.answers && result.answers.length > 0 && (
                    <div className="card">
                      <div className="card-header py-3 bg-base">
                        <h6 className="mb-0 fw-semibold">Answer Details</h6>
                      </div>
                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="bg-base">
                              <tr>
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Question</th>
                                <th className="px-3 py-2">Selected Answer</th>
                                <th className="px-3 py-2">Correct Answer</th>
                                <th className="px-3 py-2">Points</th>
                                <th className="px-3 py-2">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.answers.map((answer, index) => (
                                <tr
                                  key={answer.id}
                                  className={
                                    answer.isCorrect
                                      ? "bg-success-subtle"
                                      : "bg-danger-subtle"
                                  }
                                >
                                  <td className="px-3 py-2">{index + 1}</td>
                                  <td className="px-3 py-2">
                                    {answer.question?.questionText ||
                                      "Question not available"}
                                  </td>
                                  <td className="px-3 py-2">
                                    {answer.question?.options &&
                                      answer.question.options[
                                        answer.selectedOption
                                      ]}
                                  </td>
                                  <td className="px-3 py-2">
                                    {answer.question?.options &&
                                      answer.question.options[
                                        answer.question.correctAnswer
                                      ]}
                                  </td>
                                  <td className="px-3 py-2">
                                    {answer.pointsEarned} /{" "}
                                    {answer.question?.points || 1}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span
                                      className={`badge ${
                                        answer.isCorrect
                                          ? "bg-success"
                                          : "bg-danger"
                                      }`}
                                    >
                                      {answer.isCorrect
                                        ? "Correct"
                                        : "Incorrect"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamResultDetailsLayer;
